import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../contexts/AuthContext";
import { Button } from "../components/ui/button";
import { Badge } from "../components/ui/badge";
import axios from "axios";
import {
  LogOut, Trash2, Mail, Phone, Building2, MapPin,
  Inbox, Briefcase, Users,
} from "lucide-react";

const API = process.env.REACT_APP_BACKEND_URL;

export default function AdminDashboard() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [submissions, setSubmissions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState("all");

  useEffect(() => {
    fetchSubmissions();
  }, []);

  const fetchSubmissions = async () => {
    try {
      const { data } = await axios.get(`${API}/api/admin/submissions`, {
        withCredentials: true,
      });
      setSubmissions(data);
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const deleteSubmission = async (id) => {
    try {
      await axios.delete(`${API}/api/admin/submissions/${id}`, {
        withCredentials: true,
      });
      setSubmissions(submissions.filter((s) => s.id !== id));
    } catch (err) {
      console.error(err);
    }
  };

  const updateStatus = async (id, status) => {
    try {
      await axios.patch(
        `${API}/api/admin/submissions/${id}`,
        { status },
        { withCredentials: true }
      );
      setSubmissions(
        submissions.map((s) => (s.id === id ? { ...s, status } : s))
      );
    } catch (err) {
      console.error(err);
    }
  };

  const handleLogout = async () => {
    await logout();
    navigate("/admin/login");
  };

  const filtered =
    activeTab === "all"
      ? submissions
      : submissions.filter((s) => s.submission_type === activeTab);

  const contactCount = submissions.filter(
    (s) => s.submission_type === "contact"
  ).length;
  const careerCount = submissions.filter(
    (s) => s.submission_type === "career"
  ).length;

  return (
    <div className="min-h-screen bg-[#0A1628]">
      {/* Top bar */}
      <div className="border-b border-white/10">
        <div className="max-w-7xl mx-auto px-6 md:px-12 h-16 flex items-center justify-between">
          <h1 className="text-white font-bold text-lg">
            Energy<span className="text-[#00D68F]">X</span> Dashboard
          </h1>
          <div className="flex items-center gap-4">
            <span className="text-white/60 text-sm hidden sm:block">
              {user?.email}
            </span>
            <Button
              onClick={handleLogout}
              variant="ghost"
              size="sm"
              data-testid="logout-btn"
              className="text-white/60 hover:text-white hover:bg-white/5"
            >
              <LogOut size={16} className="mr-2" /> Logout
            </Button>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-6 md:px-12 py-8">
        {/* Header */}
        <div className="mb-8">
          <h2 className="text-3xl font-bold text-white mb-2">Submissions</h2>
          <p className="text-white/50 text-sm">
            Manage contact and career enquiries
          </p>
        </div>

        {/* Filter tabs */}
        <div className="flex gap-2 mb-8">
          {[
            { key: "all", label: "All", count: submissions.length, icon: Inbox },
            { key: "contact", label: "Contact", count: contactCount, icon: Users },
            { key: "career", label: "Careers", count: careerCount, icon: Briefcase },
          ].map((tab) => {
            const Icon = tab.icon;
            return (
              <button
                key={tab.key}
                onClick={() => setActiveTab(tab.key)}
                data-testid={`tab-${tab.key}`}
                className={`flex items-center gap-2 px-4 py-2.5 text-sm font-bold transition-all ${
                  activeTab === tab.key
                    ? "bg-[#00D68F] text-[#0A1628]"
                    : "bg-white/5 text-white/60 hover:bg-white/10"
                }`}
              >
                <Icon size={14} />
                {tab.label} ({tab.count})
              </button>
            );
          })}
        </div>

        {/* Content */}
        {loading ? (
          <div className="text-white/40 text-center py-12">Loading...</div>
        ) : filtered.length === 0 ? (
          <div
            className="text-white/40 text-center py-12 bg-white/5 border border-white/10"
            data-testid="no-submissions"
          >
            No submissions yet
          </div>
        ) : (
          <div className="space-y-4">
            {filtered.map((s) => (
              <div
                key={s.id}
                className="bg-white/5 backdrop-blur-xl border border-white/10 p-6"
                data-testid={`submission-${s.id}`}
              >
                <div className="flex items-start justify-between mb-4">
                  <div>
                    <div className="flex items-center gap-3 mb-1 flex-wrap">
                      <h3 className="text-white font-bold text-lg">
                        {s.name}
                      </h3>
                      <Badge
                        className={`text-xs ${
                          s.submission_type === "career"
                            ? "bg-purple-500/20 text-purple-300 hover:bg-purple-500/30"
                            : "bg-[#00D68F]/20 text-[#00D68F] hover:bg-[#00D68F]/30"
                        }`}
                      >
                        {s.submission_type}
                      </Badge>
                      <Badge
                        className={`text-xs ${
                          s.status === "new"
                            ? "bg-blue-500/20 text-blue-300 hover:bg-blue-500/30"
                            : s.status === "reviewed"
                            ? "bg-yellow-500/20 text-yellow-300 hover:bg-yellow-500/30"
                            : "bg-green-500/20 text-green-300 hover:bg-green-500/30"
                        }`}
                      >
                        {s.status}
                      </Badge>
                    </div>
                    <div className="flex flex-wrap gap-4 text-white/50 text-sm mt-2">
                      {s.email && (
                        <span className="flex items-center gap-1">
                          <Mail size={12} /> {s.email}
                        </span>
                      )}
                      {s.phone && (
                        <span className="flex items-center gap-1">
                          <Phone size={12} /> {s.phone}
                        </span>
                      )}
                      {s.company && (
                        <span className="flex items-center gap-1">
                          <Building2 size={12} /> {s.company}
                        </span>
                      )}
                      {s.plant_location && (
                        <span className="flex items-center gap-1">
                          <MapPin size={12} /> {s.plant_location}
                        </span>
                      )}
                    </div>
                  </div>
                  <div className="flex gap-2 shrink-0">
                    <select
                      value={s.status}
                      onChange={(e) => updateStatus(s.id, e.target.value)}
                      data-testid={`status-${s.id}`}
                      className="bg-white/5 border border-white/10 text-white text-xs px-2 py-1 focus:outline-none"
                    >
                      <option value="new">New</option>
                      <option value="reviewed">Reviewed</option>
                      <option value="contacted">Contacted</option>
                    </select>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => deleteSubmission(s.id)}
                      data-testid={`delete-${s.id}`}
                      className="text-red-400 hover:text-red-300 hover:bg-red-500/10"
                    >
                      <Trash2 size={14} />
                    </Button>
                  </div>
                </div>
                {s.message && (
                  <p className="text-white/60 text-sm mt-2">{s.message}</p>
                )}
                {s.monthly_bill && (
                  <p className="text-white/40 text-xs mt-2">
                    Monthly Bill: Rs. {s.monthly_bill}
                  </p>
                )}
                <p className="text-white/30 text-xs mt-3">
                  {new Date(s.created_at).toLocaleString()}
                </p>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
